import { PlayIcon } from 'lucide-react-native';
import { useState } from 'react';
import { Separator, View, type ViewProps, XStack, YStack } from 'tamagui';

import { Animations } from '../../components/Animations';
import { Button, ButtonVariant } from '../../components/buttons/Button';
import {
    BodyLargeEmphasized,
    BodyLargeRegular,
    BodySmallRegular,
    MonoLabelRegular,
} from '../../components/text/Typography';

const AnimatedCard = ({
    title,
    description,
    preBuiltAnimation,
}: {
    title: string;
    description: string;
    preBuiltAnimation: object;
}) => {
    const [runCount, setRunCount] = useState(0);

    return (
        <XStack gap={16}>
            <Button
                variant={ButtonVariant.PRIMARY}
                text="Play"
                icon={PlayIcon}
                width="auto"
                onPress={() => {
                    setRunCount((count) => count + 1);
                }}
            />
            <View
                key={runCount}
                flex={1}
                borderRadius={12}
                borderWidth={1}
                borderColor="$gray400"
                backgroundColor="$gray100"
                padding={16}
                {...preBuiltAnimation}
            >
                <YStack gap={8}>
                    <XStack gap={16} alignItems="center">
                        <BodyLargeEmphasized>{title}</BodyLargeEmphasized>
                        <BodySmallRegular color="$gray1000">
                            {description}
                        </BodySmallRegular>
                    </XStack>
                    <MonoLabelRegular color="$gray1100">
                        {`<View {...Animations.${title}} />`}
                    </MonoLabelRegular>
                </YStack>
            </View>
        </XStack>
    );
};

type AnimationProp = ViewProps['animation'];

const AnimationTimingCard = ({ animation }: { animation: AnimationProp }) => {
    const animationName = animation?.toString() ?? '';
    const [runCount, setRunCount] = useState(0);
    const isToggled = runCount % 2 === 0;

    const translateY = ['tooltip', 'sheet', 'bouncy'].includes(animationName)
        ? -20
        : 0;
    const translateX = animationName === 'slideScreen' ? -50 : 0;

    return (
        <XStack gap={16}>
            <Button
                variant={ButtonVariant.PRIMARY}
                text="Play"
                icon={PlayIcon}
                width="auto"
                onPress={() => {
                    setRunCount((count) => count + 1);
                }}
            />
            <YStack
                flex={1}
                gap={8}
                padding={16}
                borderRadius={12}
                borderWidth={1}
                borderColor="$gray400"
                animation={animation}
                animateOnly={['transform', 'backgroundColor']}
                transform={[
                    { scale: isToggled ? 1 : 0.9 },
                    { translateY: isToggled ? 0 : translateY },
                    { translateX: isToggled ? 0 : translateX },
                ]}
                backgroundColor={isToggled ? '$green100' : '$green400'}
            >
                <BodyLargeEmphasized>{animationName}</BodyLargeEmphasized>
                <YStack>
                    <MonoLabelRegular color="$gray1100">
                        {`<View`}
                    </MonoLabelRegular>
                    <MonoLabelRegular color="$gray1100">
                        {`  animation="${animationName}"`}
                    </MonoLabelRegular>
                    <MonoLabelRegular color="$gray1100">
                        {`  animateOnly={[...]} // list props to animate (for perf)`}
                    </MonoLabelRegular>
                    <MonoLabelRegular color="$gray1100">
                        {`/>`}
                    </MonoLabelRegular>
                </YStack>
            </YStack>
        </XStack>
    );
};

export const AnimationsGallery = () => {
    return (
        <YStack gap={16}>
            <BodyLargeRegular>
                Pre-Built Animations (not just timing, but animated styles like
                opacity, transform, etc.)
            </BodyLargeRegular>
            <YStack gap={48}>
                <AnimatedCard
                    title="FadeIn"
                    description="Fast opacity transition."
                    preBuiltAnimation={Animations.FadeIn}
                />
                <AnimatedCard
                    title="FadeInSlow"
                    description="Slower opacity transition."
                    preBuiltAnimation={Animations.FadeInSlow}
                />
                <AnimatedCard
                    title="FadeInFromAbove"
                    description="Slides down while fading in."
                    preBuiltAnimation={Animations.FadeInFromAbove}
                />
                <AnimatedCard
                    title="FadeInFromBelow"
                    description="Slides up while fading in."
                    preBuiltAnimation={Animations.FadeInFromBelow}
                />
                <AnimatedCard
                    title="SlowlyFadeInFromBelow"
                    description="Lazy spring from below with subtle scaling."
                    preBuiltAnimation={Animations.SlowlyFadeInFromBelow}
                />
            </YStack>
            <Separator marginVertical={48} />
            <BodyLargeRegular>Pre-Defined Animation Settings</BodyLargeRegular>
            <BodySmallRegular>
                General purpose animations that can be used for any component
            </BodySmallRegular>
            {/* Link to docs */}
            <YStack gap={32}>
                <AnimationTimingCard animation="quick" />
                <AnimationTimingCard animation="quicker" />
                <AnimationTimingCard animation="slow" />
                <AnimationTimingCard animation="lazy" />
                <AnimationTimingCard animation="bouncy" />
            </YStack>
            <BodySmallRegular>
                Functional animations for specific components
            </BodySmallRegular>
            <YStack gap={32}>
                <AnimationTimingCard animation="button" />
                <AnimationTimingCard animation="tooltip" />
                <AnimationTimingCard animation="sheet" />
                <AnimationTimingCard animation="slideScreen" />
            </YStack>
        </YStack>
    );
};
